/**
 * ŠABLÓNA "template_2" – zdieľaný komponent obrazovky (landscape aj portrait).
 * Svetlý "Restaurant" dizajn: čierna hlavička s logom a názvom, zlaté akcenty,
 * karty s číselným badge a GLUTEN FREE odznakom, zlatý alergénový a bežiaci pruh.
 *
 * Používa rovnaký dátový hook ako template_1 (useDailyMenuScreenData) – líši sa len vzhľad.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useDailyMenuScreenData,
  COUNTDOWN_INTERVAL_MS,
} from "app/hooks/dsok/useDailyMenuScreenData";
import { useRotatingLocale } from "../../useRotatingLocale";
import ErrorView from "../../ErrorView";
import Template2ProductCard from "./Template2ProductCard";
import { resolveLocaleValue } from "app/utils/resolveLocaleValue";
import { formatAllergens } from "../../helpers/formatAllergens";
import { formatItemQuantity } from "../../helpers/formatItemQuantity";
import "./template2.css";

/** Jedna položka týždenného pohľadu: [číslo] [gramáž] názov (alergény). */
function WeeklyItem({ p, index, locale, defaultLocale }) {
  const name = resolveLocaleValue(p?.title, locale, defaultLocale) || "";
  const weight = formatItemQuantity(p);
  const allergens = formatAllergens(p?.allergens);
  return (
    <li className={p?.isActive === false ? "is-inactive" : undefined}>
      <span className="dm-t2__weekly-num">{p?.ordinal ?? index + 1}</span>
      <span className="dm-t2__weekly-item-text">
        {weight && <span className="dm-t2__weekly-weight">{weight} </span>}
        {name}
        {allergens && (
          <span className="dm-t2__weekly-allergens"> {allergens}</span>
        )}
      </span>
    </li>
  );
}

const DATE_TIME_LOCALE = "sk-SK";
const MEALS_PAGE_INTERVAL_MS = 6000;

/** Sekčné popisky týždenného pohľadu (fallback SK). */
const WEEKLY_LABELS = {
  soups: { "sk-SK": "Polievky", "cs-CZ": "Polévky", "en-US": "Soups", "de-DE": "Suppen" },
  meals: {
    "sk-SK": "Hlavné jedlá",
    "cs-CZ": "Hlavní jídla",
    "en-US": "Main dishes",
    "de-DE": "Hauptgerichte",
  },
};
const WEEKLY_DAY_FALLBACK = {
  monday: "Pondelok",
  tuesday: "Utorok",
  wednesday: "Streda",
  thursday: "Štvrtok",
  friday: "Piatok",
  saturday: "Sobota",
  sunday: "Nedeľa",
};
const pickWeeklyLabel = (dict, locale) =>
  dict[locale] || dict["sk-SK"] || Object.values(dict)[0] || "";
const formatWeeklyDayHeading = (day, locale) => {
  if (day?.date instanceof Date && !Number.isNaN(day.date.getTime())) {
    try {
      const wd = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(day.date);
      const dm = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
        .format(day.date)
        .replace(/\s/g, "");
      return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${dm}`;
    } catch {
      /* fallthrough */
    }
  }
  return WEEKLY_DAY_FALLBACK[day?.dayKey] || day?.dayKey || "";
};

function formatDateOnly(date) {
  try {
    return new Intl.DateTimeFormat(DATE_TIME_LOCALE, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .format(date)
      .replace(/\s/g, "");
  } catch {
    return date.toLocaleDateString(DATE_TIME_LOCALE).replace(/\s/g, "");
  }
}

export default function Template2Screen({ orientation = "landscape" }) {
  const isPortrait = orientation === "portrait";
  const { customerCode, locationCode } = useParams();
  const [searchParams] = useSearchParams();
  const env = searchParams.get("env") ?? "";

  const {
    loading,
    error,
    refetch,
    logo,
    data,
    activeMenuType,
    defaultLocale,
    supportedLocales,
    rotateMs,
    soups,
    meals,
    getTitle,
    getAllergensLegend,
    getTickerText,
    showAllergens,
    showDescription,
    showPrices,
    productDisplayType,
    soupPlaceholderImage,
    mealPlaceholderImage,
    weekDays,
    weeklyRotation,
  } = useDailyMenuScreenData({
    customerCode,
    locationCode,
    env,
    menuId: "default",
    orientation,
  });

  const [locale] = useRotatingLocale(supportedLocales, rotateMs);
  const [timeTick, setTimeTick] = useState(0);
  const [mealPageIndex, setMealPageIndex] = useState(0);

  // Striedanie denného a týždenného menu – LEN portrait (podľa nastavenia).
  const weeklyRotationActive =
    isPortrait &&
    weeklyRotation?.enabled === true &&
    (weekDays?.length ?? 0) > 0;
  const [showWeekly, setShowWeekly] = useState(false);
  useEffect(() => {
    if (!weeklyRotationActive) {
      setShowWeekly(false);
      return undefined;
    }
    const intervalMs = Math.max(1, weeklyRotation.intervalSec) * 1000;
    const id = setInterval(() => setShowWeekly((s) => !s), intervalMs);
    return () => clearInterval(id);
  }, [weeklyRotationActive, weeklyRotation?.intervalSec]);

  useEffect(() => {
    const id = setInterval(() => setTimeTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now = useMemo(() => new Date(), [timeTick]); // eslint-disable-line react-hooks/exhaustive-deps -- živé hodiny
  const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}`;
  const dateStr = formatDateOnly(now);

  const title = useMemo(() => getTitle(locale), [getTitle, locale]);
  const allergensLegend = useMemo(
    () => getAllergensLegend(locale),
    [getAllergensLegend, locale],
  );
  const tickerText = useMemo(
    () => getTickerText(locale),
    [getTickerText, locale],
  );

  // Režim zobrazenia: primárne z nastavení (productDisplayType podľa orientácie),
  // voliteľný override cez URL ?display=grid|list (na náhľad/testovanie).
  const displayParam = searchParams.get("display");
  const isGrid = displayParam
    ? displayParam === "grid"
    : productDisplayType === "grid";
  // Počet jedál na stránku: primárne z nastavení (Max. jedál portrait/landscape),
  // fallback na predvolené hodnoty šablóny.
  const maxMealsSetting = Number(
    isPortrait
      ? data?.settings?.maxMealsPerPagePortrait
      : data?.settings?.maxMealsPerPageLandscape,
  );
  const mealsPerPage =
    Number.isFinite(maxMealsSetting) && maxMealsSetting > 0
      ? maxMealsSetting
      : isGrid
        ? isPortrait
          ? 6
          : 8
        : isPortrait
          ? 6
          : 5;
  const mealPageCount = Math.max(1, Math.ceil(meals.length / mealsPerPage));
  const mealPageStart = mealPageIndex * mealsPerPage;
  const visibleMeals = meals.slice(mealPageStart, mealPageStart + mealsPerPage);

  useEffect(() => {
    setMealPageIndex((i) => (mealPageCount <= 1 ? 0 : Math.min(i, mealPageCount - 1)));
  }, [mealPageCount]);

  useEffect(() => {
    if (mealPageCount <= 1) return undefined;
    const id = setInterval(() => {
      setMealPageIndex((i) => (i + 1) % mealPageCount);
    }, MEALS_PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mealPageCount]);

  const hasTicker = Boolean(tickerText && tickerText.trim());
  const tickerSegment = hasTicker ? `${tickerText.trim()}   •   ` : "";
  const showAllergensBar = showAllergens && allergensLegend.length > 0;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100vh",
          margin: 0,
          background: "#000",
          color: "#888",
          fontSize: "1.5rem",
        }}
      >
        {logo ? (
          <img
            src={logo}
            alt=""
            style={{
              maxWidth: "40vw",
              maxHeight: "40vh",
              objectFit: "contain",
            }}
          />
        ) : (
          "Loading…"
        )}
      </div>
    );
  }
  if (error) {
    return (
      <ErrorView
        error={error}
        onRetry={refetch}
        countdownIntervalMs={COUNTDOWN_INTERVAL_MS}
        className="dm-t2"
      />
    );
  }

  const cardProps = {
    locale,
    defaultLocale,
    showDescription,
    showPrices,
    badgeOnImage: isGrid,
    orientation,
  };

  return (
    <div className={`dm-t2 dm-t2--${isPortrait ? "portrait" : "landscape"}`}>
      <header className="dm-t2__header">
        <div className="dm-t2__brand">
          {logo ? (
            <img className="dm-t2__brand-logo" src={logo} alt="" />
          ) : (
            <span className="dm-t2__brand-text">RESTAURANT</span>
          )}
        </div>
        <div className="dm-t2__title-wrap">
          <h1 className="dm-t2__title">{title}</h1>
          {dateStr && <span className="dm-t2__date">{dateStr}</span>}
        </div>
        <div className="dm-t2__clock">
          <div className="dm-t2__clock-row">
            <svg
              className="dm-t2__clock-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 7v5l3.5 2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="dm-t2__time">{timeStr}</span>
          </div>
          {activeMenuType?.label && (
            <span className="dm-t2__type">{activeMenuType.label}</span>
          )}
        </div>
      </header>

      {weeklyRotationActive && showWeekly ? (
        <div className="dm-t2__body dm-t2__weekly-body">
          <div className="dm-t2__weekly-scroll">
            <div
              className="dm-t2__weekly-track"
              style={{
                animationDuration: `${Math.max(24, weekDays.length * 9)}s`,
              }}
            >
              {[0, 1].map((copy) => (
                <div
                  className="dm-t2__weekly-block"
                  key={copy}
                  aria-hidden={copy === 1 ? "true" : undefined}
                >
                  {weekDays.map((day) => (
                    <section
                      className="dm-t2__weekly-day"
                      key={`${copy}-${day.dayKey}`}
                    >
                      <h2 className="dm-t2__weekly-day-title">
                        {formatWeeklyDayHeading(day, locale)}
                      </h2>
                      {day.soups.length > 0 && (
                        <div className="dm-t2__weekly-group">
                          <div className="dm-t2__weekly-label">
                            {pickWeeklyLabel(WEEKLY_LABELS.soups, locale)}
                          </div>
                          <ul className="dm-t2__weekly-list">
                            {day.soups.map((p, i) => (
                              <WeeklyItem
                                key={`s-${i}`}
                                p={p}
                                index={i}
                                locale={locale}
                                defaultLocale={defaultLocale}
                              />
                            ))}
                          </ul>
                        </div>
                      )}
                      {day.meals.length > 0 && (
                        <div className="dm-t2__weekly-group">
                          <div className="dm-t2__weekly-label">
                            {pickWeeklyLabel(WEEKLY_LABELS.meals, locale)}
                          </div>
                          <ul className="dm-t2__weekly-list">
                            {day.meals.map((p, i) => (
                              <WeeklyItem
                                key={`m-${i}`}
                                p={p}
                                index={i}
                                locale={locale}
                                defaultLocale={defaultLocale}
                              />
                            ))}
                          </ul>
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
      <div className={`dm-t2__body${isGrid ? " dm-t2__body--grid" : ""}`}>
        <div className="dm-t2__col dm-t2__col--soups">
          <h2 className="dm-t2__section-title">
            <svg
              className="dm-t2__section-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z" />
              <path d="M7 21h10" />
              <path d="M19.5 12 22 6" />
              <path d="M16.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.73 1.62" />
              <path d="M11.25 3c.27.1.8.53.74 1.36-.05.83-.93 1.2-.98 2.02-.06.78.33 1.24.72 1.62" />
              <path d="M6.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.74 1.62" />
            </svg>
            Polievky
          </h2>
          <div
            className={isGrid ? "dm-t2__grid-items" : "dm-t2__soups"}
            style={isGrid ? { "--t2-cols": 2 } : undefined}
          >
            {soups.map((item) => (
              <Template2ProductCard
                key={item.id ?? item.ordinal}
                item={item}
                placeholderImage={soupPlaceholderImage}
                {...cardProps}
              />
            ))}
          </div>
        </div>

        <div className="dm-t2__col dm-t2__col--meals">
          <h2 className="dm-t2__section-title">
            <svg
              className="dm-t2__section-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
              <path d="M7 2v20" />
              <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
            </svg>
            Hlavné jedlá
          </h2>
          <div
            className={isGrid ? "dm-t2__grid-items" : "dm-t2__meals"}
            style={isGrid ? { "--t2-cols": 4 } : undefined}
          >
            {visibleMeals.map((item) => (
              <Template2ProductCard
                key={item.id ?? item.ordinal}
                item={item}
                placeholderImage={mealPlaceholderImage}
                {...cardProps}
              />
            ))}
          </div>
        </div>
      </div>
      )}

      {showAllergensBar && (
        <div className="dm-t2__allergens">
          <span className="dm-t2__allergens-label">Zoznam alergénov</span>
          {[...allergensLegend]
            .sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0))
            .map((row, index, arr) => (
              <span key={row?.number ?? row?.text ?? index}>
                {row?.number != null && (
                  <span className="dm-t2__allergens-num">{row.number}</span>
                )}
                {row?.text}
                {index < arr.length - 1 && (
                  <span className="dm-t2__allergens-sep">|</span>
                )}
              </span>
            ))}
        </div>
      )}

      {hasTicker && (
        <div className="dm-t2__ticker">
          <svg
            className="dm-t2__ticker-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m3 11 18-5v12L3 14v-3z" />
            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
          </svg>
          <div className="dm-t2__ticker-track-wrap">
            <div className="dm-t2__ticker-track">
              {[0, 1].map((blockIndex) => (
                <span key={blockIndex} className="dm-t2__ticker-block">
                  {Array.from({ length: 50 }, (_, i) => (
                    <span key={i} className="dm-t2__ticker-segment">
                      {tickerSegment}
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
